import { z } from 'zod'

import { ApiClientError } from '@ems/api-client/client'
import { toInstant, toLocalInput } from '@ems/contracts/timezones'
import { formatMoney } from '@ems/ui/lib/format'

import { windowDate } from '#admin/features/ticket-types/api.ts'
import { api } from '#admin/lib/api-client.ts'

/**
 * The voucher catalog: discount codes an applicant or an operator applies to a registration.
 *
 * A voucher's value is copied onto the registration's lines when it is applied, so editing it
 * reprices nothing already discounted. The dialog says so; the API is the one that holds to it.
 */

// ponytail: mirrors `VOUCHER_DISCOUNT_TYPES` in @ems/db, which admin does not depend on.
export const DISCOUNT_TYPES = ['percentage', 'fixed_amount', 'override_price'] as const

export type DiscountType = (typeof DISCOUNT_TYPES)[number]

const voucherSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  discountType: z.enum(DISCOUNT_TYPES),
  discountValue: z.number(),
  /** The currency of `discountValue` for the two amount types; null for `percentage`. */
  currency: z.string().nullable(),
  maxUses: z.number().nullable(),
  usedCount: z.number(),
  validFrom: z.string().nullable(),
  validUntil: z.string().nullable(),
  isActive: z.boolean(),
  /** Empty means every ticket type. */
  ticketTypeIds: z.array(z.string()),
})

export type Voucher = z.infer<typeof voucherSchema>

const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {})
const base = '/v1/ticket/admin/vouchers'

/** Every live voucher, inactive and expired ones included, by code. */
const redemptionSchema = z.object({
  id: z.string(),
  registrationId: z.string(),
  registrationCode: z.string().nullable(),
  registrationStatus: z.string(),
  contactName: z.string(),
  contactEmail: z.string(),
  discountAmount: z.number(),
  currency: z.string(),
  createdAt: z.string(),
})

export type VoucherRedemption = z.infer<typeof redemptionSchema>

/** The registrations that use the voucher, newest first. */
export function fetchVoucherRedemptions(id: string, signal?: AbortSignal) {
  return api.get(`${base}/${id}/redemptions`, {
    schema: z.object({ items: z.array(redemptionSchema) }),
    ...withSignal(signal),
  })
}

export function fetchVouchers(signal?: AbortSignal) {
  return api.get(base, {
    schema: z.object({ items: z.array(voucherSchema) }),
    ...withSignal(signal),
  })
}

/** What the dialog edits: every input as its own string, as the controls hold it. */
export type VoucherDraft = {
  code: string
  name: string
  discountType: DiscountType
  /** A decimal string: a percentage, an amount off, or the price a line is set to. */
  discountValue: string
  /** Kept while a percentage is chosen, so switching back does not lose it; sent only for amounts. */
  currency: string
  /** Empty means unlimited. */
  maxUses: string
  /** `datetime-local` values, in the edition's zone. Empty means no limit. */
  validFrom: string
  validUntil: string
  isActive: boolean
  ticketTypeIds: string[]
}

export function draftOf(voucher: Voucher | null, currency: string, zone: string): VoucherDraft {
  if (voucher === null) {
    return {
      code: '',
      name: '',
      discountType: 'percentage',
      discountValue: '',
      currency,
      maxUses: '',
      validFrom: '',
      validUntil: '',
      isActive: true,
      ticketTypeIds: [],
    }
  }
  return {
    code: voucher.code,
    name: voucher.name,
    discountType: voucher.discountType,
    discountValue: String(voucher.discountValue),
    currency: voucher.currency ?? currency,
    maxUses: voucher.maxUses === null ? '' : String(voucher.maxUses),
    validFrom: voucher.validFrom === null ? '' : toLocalInput(voucher.validFrom, zone),
    validUntil: voucher.validUntil === null ? '' : toLocalInput(voucher.validUntil, zone),
    isActive: voucher.isActive,
    ticketTypeIds: [...voucher.ticketTypeIds],
  }
}

// The column's limit: `discount_value` is `numeric(18,4)`.
const AMOUNT = /^\d{1,14}(?:\.\d{1,4})?$/

/** What is wrong with a draft, by field. Empty when it can be sent. */
export function draftIssues(draft: VoucherDraft): Record<string, string> {
  const issues: Record<string, string> = {}
  if (draft.code.trim() === '') issues['code'] = 'Give the voucher a code.'
  if (draft.name.trim() === '') issues['name'] = 'Give the voucher a name.'
  const value = draft.discountValue.trim()
  if (!AMOUNT.test(value) || Number(value) <= 0) {
    issues['discountValue'] =
      draft.discountType === 'percentage'
        ? 'Enter a percentage above 0, such as 20.'
        : 'Enter an amount above 0, such as 500000.'
  } else if (draft.discountType === 'percentage' && Number(value) > 100) {
    issues['discountValue'] = 'A percentage is at most 100.'
  }
  if (draft.maxUses.trim() !== '' && !/^[1-9]\d*$/.test(draft.maxUses.trim())) {
    issues['maxUses'] = 'Enter a whole number of uses, or leave it empty for unlimited.'
  }
  // `datetime-local` values sort as strings, and both are in the same zone.
  if (draft.validFrom !== '' && draft.validUntil !== '' && draft.validUntil <= draft.validFrom) {
    issues['validUntil'] = 'The voucher must expire after it starts.'
  }
  return issues
}

/**
 * The wire body. Every field is sent, so an emptied input clears its column (`null`) rather than
 * being left out and kept. The scope is sorted, so the same set compares equal in `changesOf`.
 */
export function bodyOf(draft: VoucherDraft, zone: string) {
  const instant = (value: string) => (value === '' ? null : toInstant(value, zone))
  return {
    code: draft.code.trim(),
    name: draft.name.trim(),
    discountType: draft.discountType,
    discountValue: draft.discountValue.trim(),
    currency: draft.discountType === 'percentage' ? null : draft.currency,
    maxUses: draft.maxUses.trim() === '' ? null : Number(draft.maxUses),
    validFrom: instant(draft.validFrom),
    validUntil: instant(draft.validUntil),
    isActive: draft.isActive,
    ticketTypeIds: draft.ticketTypeIds.toSorted(),
  }
}

export type VoucherBody = ReturnType<typeof bodyOf>

/**
 * An edit's PATCH: only the fields the operator changed, as on ticket types — the whole body would
 * write back the copy taken when the dialog opened.
 */
export function changesOf(
  voucher: Voucher,
  draft: VoucherDraft,
  zone: string,
): Partial<VoucherBody> {
  const before: Record<string, unknown> = bodyOf(draftOf(voucher, draft.currency, zone), zone)
  return Object.fromEntries(
    Object.entries(bodyOf(draft, zone)).filter(
      // JSON, for the scope: two arrays with the same ids are the same scope.
      ([key, value]) => JSON.stringify(before[key]) !== JSON.stringify(value),
    ),
  )
}

export function createVoucher(body: VoucherBody) {
  return api.post(base, { body, schema: voucherSchema })
}

export function updateVoucher(id: string, body: Partial<VoucherBody>) {
  return api.patch(`${base}/${id}`, { body, schema: voucherSchema })
}

export function deleteVoucher(id: string) {
  return api.delete(`${base}/${id}`, { schema: z.undefined() })
}

const REFUSABLE: readonly (keyof VoucherDraft)[] = [
  'code',
  'name',
  'discountValue',
  'currency',
  'maxUses',
  'validUntil',
  'ticketTypeIds',
]

/**
 * Which dialog field a refusal belongs beside. The API names it as `detail.field`, except for a
 * taken code, which carries the code. `undefined` sends it to a toast instead.
 */
export function refusalField(error: unknown): keyof VoucherDraft | undefined {
  if (!(error instanceof ApiClientError)) return undefined
  const detail = error.detail ?? {}
  const field = REFUSABLE.find((name) => name === detail['field'])
  if (field !== undefined) return field
  if (error.code === 'conflict' && 'code' in detail) return 'code'
  return undefined
}

/** "20%", "IDR 500.000 off", or "IDR 1.500.000 flat" — the price a line is set to. */
export function discountLabel(
  voucher: Pick<Voucher, 'discountType' | 'discountValue' | 'currency'>,
): string {
  if (voucher.discountType === 'percentage') return `${voucher.discountValue}%`
  // The database refuses an amount voucher without a currency; v1 rows awaiting the ETL are the
  // exception, and show the bare number rather than a guessed currency.
  const amount =
    voucher.currency === null
      ? voucher.discountValue.toLocaleString()
      : formatMoney(voucher.discountValue, voucher.currency)
  return voucher.discountType === 'fixed_amount' ? `${amount} off` : `${amount} flat`
}

/** "valid", "starts 01 Mar, 09:00", "expired 01 Apr 2025, 09:00", or "always" — `salesWindowLabel`'s shape. */
export function validityLabel(
  voucher: Pick<Voucher, 'validFrom' | 'validUntil'>,
  now: Date,
  zone: string,
): string {
  if (voucher.validFrom === null && voucher.validUntil === null) return 'always'
  if (voucher.validFrom !== null && Date.parse(voucher.validFrom) > now.getTime()) {
    return `starts ${windowDate(voucher.validFrom, zone, now)}`
  }
  if (voucher.validUntil !== null && Date.parse(voucher.validUntil) <= now.getTime()) {
    return `expired ${windowDate(voucher.validUntil, zone, now)}`
  }
  return 'valid'
}

/** "3 of 100 used", or "3 used · unlimited". */
export function usesLabel(voucher: Pick<Voucher, 'usedCount' | 'maxUses'>): string {
  return voucher.maxUses === null
    ? `${voucher.usedCount} used · unlimited`
    : `${voucher.usedCount} of ${voucher.maxUses} used`
}
